/**
 * ScheduleInstallation — office CRUD to create an installation job
 * Route: /installations/new
 * API: POST /api/v1/installations
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AppButton, AppCard, AppField, AppInput } from "../../../shared/components/ui";
import { PageHeader } from "../../../shared/components/PageChrome";
import { IconError, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

export function ScheduleInstallation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillCustomerId = searchParams.get("customer_id") || "";
  const [customerId, setCustomerId] = useState(prefillCustomerId);
  const [customerLabel, setCustomerLabel] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [technicianId, setTechnicianId] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!prefillCustomerId) return;
    setCustomerId(prefillCustomerId);
    let cancelled = false;
    api.get<{
      customer: { id: number; customer_number: string; first_name?: string; last_name?: string };
    }>(`/customers/${prefillCustomerId}`)
      .then((d) => {
        if (cancelled || !d.customer) return;
        const name = [d.customer.first_name, d.customer.last_name].filter(Boolean).join(" ");
        setCustomerLabel(`${name || d.customer.customer_number} (#${d.customer.id})`);
      })
      .catch(() => { /* keep numeric id */ });
    return () => { cancelled = true; };
  }, [prefillCustomerId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body: Record<string, unknown> = {
        customer_id: parseInt(customerId, 10),
      };
      if (deviceId.trim()) body.device_id = parseInt(deviceId, 10);
      if (technicianId.trim()) body.technician_id = parseInt(technicianId, 10);
      if (scheduledDate) body.scheduled_date = `${scheduledDate}T09:00:00`;
      if (notes.trim()) body.notes = notes.trim();
      const res = await api.post<{ installation: { id: number } }>("/installations", body);
      navigate(`/field/installations/${res.installation.id}/scan`);
    } catch (ex: unknown) {
      setError(ex instanceof Error ? ex.message : "Could not schedule installation");
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Schedule Installation"
        subtitle="Create and route installation jobs with clear ownership and timeline."
        crumbs={[
          { label: "Installations", to: "/field/installations" },
          { label: "Schedule" },
        ]}
      />
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <AppCard title="Schedule queue context">
          <div className="pg-summary">
            <div className="row">
              <span className="k">Storyline</span>
              <span className="v">Schedule → Assign → Field evidence → Approval</span>
            </div>
            <div className="row">
              <span className="k">Queue owner</span>
              <span className="v">Office operations</span>
            </div>
            <div className="row">
              <span className="k">Execution owner</span>
              <span className="v">Field technician</span>
            </div>
            <div className="row">
              <span className="k">Outcome</span>
              <span className="v">Ready for first-token approval</span>
            </div>
          </div>
        </AppCard>
        <AppCard title="Create installation job">
          {error && (
            <div className="pg-inline-error" role="alert" style={{ marginBottom: 12 }}>
              <IconError size={14} /> {error}
            </div>
          )}
          <form onSubmit={submit}>
            <div style={{ display: "grid", gap: "1rem", maxWidth: 520 }}>
              <AppField label="Customer ID (numeric)">
                <AppInput
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  required
                  disabled={busy || Boolean(prefillCustomerId)}
                />
              </AppField>
              {customerLabel ? (
                <p className="pg-muted" style={{ margin: 0, fontSize: 13 }}>
                  Selected: {customerLabel}
                </p>
              ) : null}
              <AppField label="Device ID (optional)">
                <AppInput value={deviceId} onChange={(e) => setDeviceId(e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Technician user ID (optional)">
                <AppInput value={technicianId} onChange={(e) => setTechnicianId(e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Scheduled date">
                <AppInput type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} disabled={busy} />
              </AppField>
              <AppField label="Notes">
                <AppInput value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} />
              </AppField>
              <div style={{ display: "flex", gap: 8 }}>
                <AppButton type="submit" disabled={busy}>
                  {busy ? <><IconSpinner size={14} /> Saving…</> : "Schedule"}
                </AppButton>
                <Link to="/field/installations">
                  <AppButton type="button" variant="secondary" disabled={busy}>Cancel</AppButton>
                </Link>
              </div>
            </div>
          </form>
        </AppCard>
      </div>
    </div>
  );
}
